import { db } from './index';

// Reason: the live view used to keep exactly one cache row; browsing the archive
// makes the row count grow with navigation. Design §3.6 caps archive rows at the
// default discovery page size, so a fully-browsed discovery window fits in cache.
export const ARCHIVE_CACHE_LIMIT = 30;

// Reason: 'latest' is the offline-critical entry — it is what a cold offline
// launch of /daily renders. It is exempt from the cap no matter how stale its
// fetch timestamp is, so eviction must never treat it as just another row.
export const LIVE_BRIEFING_KEY = 'latest';

/**
 * Evict least-recently-fetched archive rows beyond the cap.
 *
 * Rows keyed by ISO date only; the live-view row is exempt. Eviction costs a
 * network round-trip on the next visit, never data — the server row stays
 * authoritative (§3.6).
 *
 * @param {number} [limit] - Maximum archive rows to keep.
 * @param {string} [protectedKey] - A key that must never be evicted, normally the
 *   date currently on screen. On a cache hit the caller renders before
 *   revalidation refreshes `fetched_at`, so the displayed row can still be the
 *   oldest by that index — evicting it would blank the page being read.
 * @returns {Promise<string[]>} The date keys that were evicted.
 */
export const enforceArchiveRetention = async (
  limit = ARCHIVE_CACHE_LIMIT,
  protectedKey = null,
) => {
  // Reason: primaryKeys() off the `fetched_at` index is ascending (oldest first)
  // and never loads payloads. Every writer of this table sets fetched_at, so no
  // row is invisible to the index.
  const keys = await db.daily_briefings.orderBy('fetched_at').primaryKeys();
  const archiveKeys = keys.filter(key => key !== LIVE_BRIEFING_KEY);
  const excess = archiveKeys.length - limit;
  if (excess <= 0) return [];
  // Reason: the protected row still COUNTS toward the cap — it is a real cached
  // row — it is only skipped when choosing victims, so the ceiling stays honest.
  const doomed = archiveKeys.filter(key => key !== protectedKey).slice(0, excess);
  await db.daily_briefings.bulkDelete(doomed);
  return doomed;
};
